// src/pages/PriceManagementPage.jsx
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { saveAs } from "file-saver";
import ShopifyService from '../services/shopifyService';
import * as XLSX from "xlsx";
import API_BASE_URL from "../config/api";
import axios from "axios";



const PriceManagementPage = () => {
    const API_BASE = API_BASE_URL || "https://medi-panel.cloud/api";


    const [vendors, setVendors] = useState([]);
    const [exportVendor, setExportVendor] = useState("");
    const [importVendor, setImportVendor] = useState("");
    const [exportMode, setExportMode] = useState("with_ids"); // with_ids | basic

    const [file, setFile] = useState(null);

    const [loadingVendors, setLoadingVendors] = useState(false);
    const [loadingExport, setLoadingExport] = useState(false);
    const [loadingImport, setLoadingImport] = useState(false);

    const [message, setMessage] = useState(null); // {type, text}
    const fileInputRef = useRef(null);


    // ---------- helpers ----------
    const setMsg = useCallback((type, text) => {
        console.log(`[${type}] ${text}`);
        setMessage({ type, text });
        // 5 saniye sonra mesajı temizle
        if (type !== 'info') {
            setTimeout(() => setMessage(null), 5000);
        }
    }, []);

    const getToken = useCallback(() => {
        // Token'ı hem sessionStorage hem localStorage'dan kontrol et
        const tokenFromSession = sessionStorage.getItem("token");
        const tokenFromLocal = localStorage.getItem("token");

        console.log("Token kontrolü - session:", !!tokenFromSession, "local:", !!tokenFromLocal);

        // Öncelik sessionStorage, sonra localStorage
        return tokenFromSession || tokenFromLocal || "";
    }, []);

    const buildAuthHeaders = useCallback(() => {
        const token = getToken();
        console.log("Header oluşturma - token:", token ? "Var" : "Yok");

        if (!token) {
            setMsg("error", "Token bulunamadı. Lütfen tekrar giriş yapın.");
            return {};
        }

        return {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        };
    }, [getToken, setMsg]);

    const buildAuthHeadersMultipart = useCallback(() => {
        const token = getToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, [getToken]);

    const safeFilename = useCallback((name) => {
        return (name || "export")
            .replace(/[\\/:*?"<>|]+/g, "_")
            .replace(/\s+/g, "_")
            .slice(0, 120);
    }, []);

    const todayStamp = useCallback(() => {
        const now = new Date();
        return `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;
    }, []);

    const busy = loadingVendors || loadingExport || loadingImport;

    // ---------- vendor load ----------
    const loadVendors = useCallback(async () => {
        try {
            setLoadingVendors(true);
            setMsg("info", "Vendor listesi Shopify'dan yükleniyor...");

            const result = await ShopifyService.getAllVendors();

            if (!result?.success) {
                throw new Error("Vendor listesi alınamadı");
            }

            const list = Array.isArray(result.vendors) ? result.vendors : [];
            list.sort((a, b) => a.localeCompare(b));

            setVendors(list);

            if (list.length > 0) {
                setExportVendor(prev => prev || list[0]);
                setImportVendor(prev => prev || list[0]);
            }

            setMsg("success", `✅ ${list.length} vendor Shopify'dan yüklendi`);

        } catch (e) {
            console.error("Vendor yükleme hatası:", e);
            setMsg("error", `❌ ${e.message}`);

            // Fallback: Test vendor listesi
            const testVendors = ["TestVendor1", "TestVendor2", "Nike", "Adidas", "Apple"];
            setVendors(testVendors);
            if (testVendors.length > 0) {
                setExportVendor(testVendors[0]);
                setImportVendor(testVendors[0]);
            }
            setMsg("warning", "Backend bağlantısı yok. Test vendor listesi kullanılıyor.");
        } finally {
            setLoadingVendors(false);
        }
    }, [setMsg]);

    // ---------- export ----------
    const exportUrl = useMemo(() => {
        if (!exportVendor) return null;

        // BACKEND UYUMU İÇİN ÖNEMLİ: 
        // Sizin backend controller'larınız /admin/pricing/excel/export kullanıyor
        // NOT: Kodunuzda /export/vendor/... kısmı yanlış olabilir

        const base = `${API_BASE}/admin/pricing/excel/export`;

        // Query parametreleri ile
        const params = new URLSearchParams();
        params.append('vendor', exportVendor);
        if (exportMode === "with_ids") {
            params.append('withIds', 'true');
        }

        return `${base}?${params.toString()}`;
    }, [API_BASE, exportVendor, exportMode]);

    const handleExport = useCallback(async () => {
        if (!exportVendor) {
            setMsg("error", "❌ Export için vendor seçin");
            return;
        }

        try {
            setLoadingExport(true);
            setMsg("info", `"${exportVendor}" için export hazırlanıyor...`);

            const token = getToken();
            if (!token) {
                throw new Error("Token bulunamadı. Lütfen tekrar giriş yapın.");
            }

            console.log("Export URL:", exportUrl);
            console.log("Token (ilk 20 karakter):", token.substring(0, 20) + "...");

            // Axios ile daha iyi hata kontrolü
            const response = await axios.get(exportUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                },
                responseType: 'blob',
                timeout: 30000 // 30 saniye timeout
            });

            console.log("Export response status:", response.status);
            console.log("Export response headers:", response.headers);

            const blob = response.data;

            if (blob.size === 0) {
                throw new Error("Boş dosya döndü");
            }

            // Dosya adı oluştur
            const filename = `${safeFilename(exportVendor)}_${exportMode === 'with_ids' ? 'with_ids_' : ''}${todayStamp()}.xlsx`;

            // file-saver ile indir
            saveAs(blob, filename);

            // ID kontrolü (opsiyonel)
            if (exportMode === "with_ids") {
                try {
                    const arrayBuffer = await blob.arrayBuffer();
                    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                    const firstRow = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] || [];

                    const hasIds = firstRow.some(header =>
                        ['product_id', 'variant_id', 'inventory_item_id'].includes(
                            String(header).toLowerCase().trim()
                        )
                    );

                    if (!hasIds) {
                        setMsg("warning", "⚠️ Export dosyasında ID kolonları bulunamadı. Backendi kontrol edin.");
                    } else {
                        setMsg("success", `✅ ID'li export indirildi: ${filename} (${(blob.size / 1024).toFixed(1)} KB)`);
                    }
                } catch (excelError) {
                    console.warn("Excel kontrol hatası:", excelError);
                    setMsg("success", `✅ Export indirildi: ${filename} (${(blob.size / 1024).toFixed(1)} KB)`);
                }
            } else {
                setMsg("success", `✅ Basic export indirildi: ${filename} (${(blob.size / 1024).toFixed(1)} KB)`);
            }

        } catch (error) {
            console.error("Export hatası detayı:", error);

            let errorMessage = `❌ ${error.message}`;

            if (error.response) {
                // Backend'den hata döndü
                console.error("Response status:", error.response.status);
                console.error("Response headers:", error.response.headers);

                if (error.response.status === 403) {
                    errorMessage = "❌ 403 Forbidden - Yetkiniz yok veya token geçersiz";
                } else if (error.response.status === 401) {
                    errorMessage = "❌ 401 Unauthorized - Oturum süresi doldu, lütfen tekrar giriş yapın";
                } else if (error.response.status === 404) {
                    errorMessage = "❌ 404 Not Found - Export endpoint'i bulunamadı. Backend controller'ı kontrol edin.";
                } else if (error.response.status === 500) {
                    errorMessage = "❌ 500 Internal Server Error - Backend hatası";
                }

                // Backend'den gelen mesajı oku
                if (error.response.data) {
                    try {
                        if (error.response.data instanceof Blob) {
                            const text = await error.response.data.text();
                            const errorData = JSON.parse(text);
                            errorMessage += `\nBackend mesajı: ${errorData.message || text.substring(0, 100)}`;
                        } else {
                            errorMessage += `\nBackend mesajı: ${error.response.data.message || JSON.stringify(error.response.data).substring(0, 100)}`;
                        }
                    } catch (parseError) {
                        console.warn("Hata mesajı parse edilemedi:", parseError);
                    }
                }
            } else if (error.request) {
                // İstek yapıldı ama yanıt alınamadı
                errorMessage = "❌ Backend'e bağlanılamadı. API sunucusu çalışıyor mu?";
            }

            setMsg("error", errorMessage);
        } finally {
            setLoadingExport(false);
        }
    }, [
        exportVendor,
        exportMode,
        exportUrl,
        getToken,
        safeFilename,
        todayStamp,
        setMsg,
    ]);

    // ---------- import ----------
    const handleImport = useCallback(async () => {
        if (!importVendor || !file) {
            setMsg("error", "❌ Import için vendor ve dosya seçin");
            return;
        }

        try {
            setLoadingImport(true);
            setMsg("info", `"${importVendor}" için import başlatılıyor...`);

            const token = getToken();
            if (!token) {
                throw new Error("Token bulunamadı");
            }

            const fd = new FormData();
            fd.append("file", file);
            fd.append("vendor", importVendor);

            console.log("Import endpoint:", `${API_BASE}/admin/pricing/excel/import`);
            console.log("Import vendor:", importVendor);
            console.log("Import file:", file.name, file.size);

            // Axios ile import
            const response = await axios.post(
                `${API_BASE}/admin/pricing/excel/import`,
                fd,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 60000 // 60 saniye timeout
                }
            );

            console.log("Import response:", response.data);

            const data = response.data;
            const msg = `✅ Import başarılı!
            
Toplam Satır: ${data.totalRows ?? "?"}
İşlenen: ${data.queuedCount ?? "?"}
Bulunamayan: ${data.skippedNotFound ?? "?"}
Geçersiz: ${data.validationFailed ?? "?"}`;

            setMsg("success", msg);

            // Temizlik
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";

        } catch (error) {
            console.error("Import hatası detayı:", error);

            let errorMessage = `❌ ${error.message}`;

            if (error.response) {
                console.error("Import response status:", error.response.status);

                if (error.response.status === 403) {
                    errorMessage = "❌ 403 Forbidden - Import için yetkiniz yok";
                } else if (error.response.status === 401) {
                    errorMessage = "❌ 401 Unauthorized - Oturum süresi doldu";
                } else if (error.response.status === 400) {
                    errorMessage = "❌ 400 Bad Request - Excel dosyası veya vendor hatalı";
                }

                // Backend hata mesajını ekle
                if (error.response.data) {
                    try {
                        errorMessage += `\nBackend: ${error.response.data.message || JSON.stringify(error.response.data).substring(0, 100)}`;
                    } catch (parseError) {
                        console.warn("Hata mesajı parse edilemedi:", parseError);
                    }
                }
            } else if (error.request) {
                errorMessage = "❌ Backend'e bağlanılamadı";
            }

            setMsg("error", errorMessage);
        } finally {
            setLoadingImport(false);
        }
    }, [API_BASE, importVendor, file, getToken, setMsg]);

    const onPickFile = useCallback(
        (e) => {
            const f = e.target.files?.[0];
            if (!f) return;

            if (!/\.(xlsx|xls)$/i.test(f.name)) {
                setMsg("error", "❌ Sadece .xlsx / .xls yükleyebilirsin");
                return;
            }

            // Dosya boyutu kontrolü (max 10MB)
            if (f.size > 10 * 1024 * 1024) {
                setMsg("error", "❌ Dosya boyutu 10MB'dan küçük olmalı");
                return;
            }

            console.log("Dosya seçildi:", f.name, f.size);
            setFile(f);
        },
        [setMsg]
    );

    // ---------- İlk yüklemede vendor listesini al ----------
    useEffect(() => {
        loadVendors();
    }, [loadVendors]);

    return (
        <div style={styles.container}>
            <h2 style={styles.title}>🏷️ Price Management</h2>

            {message?.text && (
                <div
                    style={{
                        ...styles.message,
                        background:
                            message.type === "success"
                                ? "#d4edda"
                                : message.type === "error"
                                    ? "#f8d7da"
                                    : message.type === "warning"
                                        ? "#fff3cd"
                                        : "#d1ecf1",
                        borderColor:
                            message.type === "success"
                                ? "#c3e6cb"
                                : message.type === "error"
                                    ? "#f5c6cb"
                                    : message.type === "warning"
                                        ? "#ffeaa7"
                                        : "#bee5eb",
                        whiteSpace: "pre-line",
                        color: message.type === "warning" ? "#856404" : "inherit"
                    }}
                >
                    {message.type === "success" && "✅ "}
                    {message.type === "error" && "❌ "}
                    {message.type === "warning" && "⚠️ "}
                    {message.text}
                </div>
            )}

            <div style={styles.row}>
                <button
                    onClick={loadVendors}
                    disabled={loadingVendors}
                    style={styles.btnGray}
                >
                    🔄 Vendorları Yenile
                </button>
                <div style={{ opacity: 0.8 }}>
                    {vendors.length} vendor
                    {exportVendor && ` | Seçili: ${exportVendor}`}
                </div>
            </div>

            <div style={styles.grid}>
                {/* EXPORT */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>📤 Export</h3>

                    <label style={styles.label}>Vendor</label>
                    <select
                        value={exportVendor}
                        onChange={(e) => setExportVendor(e.target.value)}
                        style={styles.select}
                        disabled={busy || vendors.length === 0}
                    >
                        {vendors.length === 0 ? (
                            <option value="">Vendor yükleniyor...</option>
                        ) : (
                            vendors.map((v) => (
                                <option key={v} value={v}>
                                    {v}
                                </option>
                            ))
                        )}
                    </select>

                    <div style={styles.radioGroup}>
                        <label style={styles.radio}>
                            <input
                                type="radio"
                                name="exportMode"
                                value="with_ids"
                                checked={exportMode === "with_ids"}
                                onChange={() => setExportMode("with_ids")}
                                disabled={busy}
                            />
                            <span>
                                <b>ID'li Export</b> (Import için gerekli)
                            </span>
                        </label>

                        <label style={styles.radio}>
                            <input
                                type="radio"
                                name="exportMode"
                                value="basic"
                                checked={exportMode === "basic"}
                                onChange={() => setExportMode("basic")}
                                disabled={busy}
                            />
                            <span>
                                <b>Basic Export</b> (ID yok)
                            </span>
                        </label>
                    </div>

                    <button
                        onClick={handleExport}
                        disabled={busy || !exportVendor}
                        style={styles.btnGreen}
                    >
                        {loadingExport ? "İndiriliyor..." : "⬇️ Excel İndir"}
                    </button>


                </div>

                {/* IMPORT */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>📥 Import</h3>

                    <label style={styles.label}>Vendor</label>
                    <select
                        value={importVendor}
                        onChange={(e) => setImportVendor(e.target.value)}
                        style={styles.select}
                        disabled={busy || vendors.length === 0}
                    >
                        {vendors.length === 0 ? (
                            <option value="">Vendor yükleniyor...</option>
                        ) : (
                            vendors.map((v) => (
                                <option key={v} value={v}>
                                    {v}
                                </option>
                            ))
                        )}
                    </select>

                    <label style={styles.label}>Excel Dosyası</label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={onPickFile}
                        disabled={busy}
                        style={styles.fileInput}
                    />

                    {file && (
                        <div style={styles.fileRow}>
                            <span>📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                            <button
                                onClick={() => {
                                    setFile(null);
                                    if (fileInputRef.current) fileInputRef.current.value = "";
                                }}
                                disabled={busy}
                                style={styles.smallBtn}
                            >
                                ✕
                            </button>
                        </div>
                    )}

                    <button
                        onClick={handleImport}
                        disabled={busy || !file || !importVendor}
                        style={styles.btnBlue}
                    >
                        {loadingImport ? "Import..." : "🚀 Import Başlat"}
                    </button>


                </div>
            </div>


        </div>
    );
};

const styles = {
    container: { maxWidth: 1100, margin: "0 auto", padding: 20 },
    title: { textAlign: "center", marginBottom: 16 },
    message: {
        padding: 14,
        borderRadius: 8,
        border: "1px solid",
        marginBottom: 16,
        fontSize: '14px'
    },
    row: {
        display: "flex",
        gap: 12,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 18
    },
    card: {
        background: "#fff",
        borderRadius: 12,
        padding: 18,
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)"
    },
    cardTitle: {
        marginTop: 0,
        marginBottom: 12
    },
    label: {
        display: "block",
        fontWeight: 700,
        marginTop: 10,
        marginBottom: 6
    },
    select: {
        width: "100%",
        padding: 10,
        borderRadius: 8,
        border: "1px solid #ddd",
        fontSize: '14px'
    },
    fileInput: {
        width: "100%",
        padding: 8,
        borderRadius: 8,
        border: "1px solid #ddd"
    },
    radioGroup: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
        marginTop: 12,
        marginBottom: 14
    },
    radio: {
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        fontSize: '14px'
    },
    btnGreen: {
        width: "100%",
        padding: 12,
        border: "none",
        borderRadius: 10,
        color: "white",
        fontWeight: 800,
        cursor: "pointer",
        background: "#28a745",
        fontSize: '14px',
        transition: 'opacity 0.2s'
    },
    btnBlue: {
        width: "100%",
        padding: 12,
        border: "none",
        borderRadius: 10,
        color: "white",
        fontWeight: 800,
        cursor: "pointer",
        background: "#007bff",
        fontSize: '14px',
        transition: 'opacity 0.2s'
    },
    btnGray: {
        padding: 10,
        border: "none",
        borderRadius: 10,
        color: "white",
        fontWeight: 800,
        cursor: "pointer",
        background: "#6c757d",
        fontSize: '14px'
    },
    smallBtn: {
        padding: "4px 10px",
        borderRadius: 8,
        border: "1px solid #ddd",
        cursor: "pointer",
        background: '#ff6b6b',
        color: 'white',
        border: 'none'
    },
    fileRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 10,
        padding: 10,
        background: "#f6f6f6",
        borderRadius: 10,
        fontSize: '14px'
    },
    hint: {
        marginTop: 12,
        fontSize: 12,
        opacity: 0.85,
        background: "#f8f9fa",
        padding: 10,
        borderRadius: 10,
        fontFamily: 'monospace'
    },
    footer: {
        marginTop: 18,
        fontSize: 12,
        opacity: 0.85,
        textAlign: "center",
        display: "grid",
        gap: 6,
        padding: 15,
        background: '#f8f9fa',
        borderRadius: 8
    },
};

export default PriceManagementPage;